import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { mutate } from 'swr';

import { ModalContent } from 'components';
import { integerToCurrency } from 'helpers';
import { returnRequestService } from 'services';
import { Price } from 'types';

interface ShippingRefundModalProps {
  consignmentCode: string;
  deliveryCost: { price: Price; refundedPrice: Price };
  onClose: () => void;
}

export function ShippingRefundModal({
  consignmentCode,
  deliveryCost,
  onClose,
}: ShippingRefundModalProps) {
  const { searchKey } = useParams<{ searchKey: string }>();
  const passwordRef = useRef<HTMLInputElement>(null);
  const refundAmountRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = (parseInt(numericValue) / 100).toFixed(2);
    return `R$ ${formattedValue.replace('.', ',')}`;
  };

  const handleCurrencyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    if (numericValue) {
      e.target.value = formatCurrency(numericValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const password = passwordRef.current?.value?.trim();
    const refundAmountStr = refundAmountRef.current?.value?.trim();

    if (!password || !refundAmountStr) return;

    const refundAmount = parseInt(refundAmountStr.replace(/\D/g, '')) || 0;

    setIsLoading(true);

    try {
      const { errors } = await returnRequestService.postConsignmentRefund({
        code: consignmentCode,
        type: 'DELIVERY_COST',
        password,
        deliveryCostRefundAmount: refundAmount,
      });

      if (!errors) {
        toast.success('Estorno de frete realizado com sucesso!');
        mutate(`consignment-and-return-${searchKey}`);
      }

      onClose();
    } catch (error) {
      console.error('Erro ao processar estorno:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalContent handleClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col w-[320px] p-2">
        <div className="text-neutral-700 pb-2">
          <p className="text-base font-bold">Realizando estorno</p>
          <p className="text-xs">Frete</p>
        </div>

        <div className="flex gap-3 pb-3">
          <div className="flex flex-col flex-grow">
            <p className="text-font10 font-bold text-neutral-500">
              Frete total
            </p>
            <p className="text-sm text-neutral-500">
              {integerToCurrency(deliveryCost.price.value)}
            </p>
          </div>
          <div className="flex flex-col flex-grow">
            <p className="text-font10 font-bold text-neutral-500">
              Frete já estornado
            </p>
            <p className="text-sm text-neutral-500">
              {integerToCurrency(deliveryCost.refundedPrice.value)}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <p className="text-xs text-neutral-500">
              Informe o valor a ser estornado agora{' '}
              <span className="text-danger-300">*</span>
            </p>
            <input
              ref={refundAmountRef}
              id="refundAmount"
              type="text"
              onChange={handleCurrencyInput}
              required
              className="h-10 px-4 py-2 rounded-lg bg-neutral-100 border border-neutral-300 text-xs
                border-solid text-neutral-500 w-full"
              placeholder="R$ 0,00"
            />
          </div>
          <div>
            <p className="text-xs text-neutral-500">
              Digite sua senha para confirmar{' '}
              <span className="text-danger-300">*</span>
            </p>
            <input
              ref={passwordRef}
              id="password"
              type="password"
              required
              className="h-10 px-4 py-2 rounded-lg bg-neutral-100 border border-neutral-300 text-xs
                border-solid text-neutral-500 w-full"
              placeholder="************"
            />
          </div>
        </div>

        <div className="flex gap-4 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex justify-center items-center flex-grow h-10 px-4 py-2 rounded-lg
              bg-neutral-500 text-sm text-neutral-100"
          >
            Voltar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex justify-center items-center flex-grow h-10 px-4 py-2 rounded-lg
              bg-primary-300 text-sm text-neutral-100 disabled:opacity-50
              disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processando...' : 'Estornar frete'}
          </button>
        </div>
      </form>
    </ModalContent>
  );
}
